import React, { useMemo, useState, useEffect } from 'react';
import { Users, User, Search, History, Calendar, UserCheck } from 'lucide-react';
import { Order, OrderSheet, SellerCustomerSummary, Store } from '../../types';
import { formatPhoneNumber } from '../../utils/formatters';

interface CustomersTabProps {
  customerSummaries: SellerCustomerSummary[];
  stores: Store[];
  orderSheets: OrderSheet[];
  customerAliases?: Record<string, string>;
  onUpdateAlias?: (phone: string, nick: string) => Promise<void>;
  onOpenAdminOrder?: (name?: string, phone?: string, order?: Order, storeId?: string, orderSheetId?: string, userId?: string) => void;
  onUpdateOrderStatus?: (order: Order) => void;
  onViewHistory?: (name: string, phone: string, userId?: string, orderSheetId?: string) => void;
}

export const CustomersTab: React.FC<CustomersTabProps> = ({
  customerSummaries,
  stores,
  orderSheets,
  customerAliases,
  onUpdateAlias,
  onOpenAdminOrder,
  onUpdateOrderStatus,
  onViewHistory,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState<string>('all');
  const [sortOption, setSortOption] = useState<'revenue' | 'name' | 'latest'>('latest');
  const [showMembersOnly, setShowMembersOnly] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStoreId, sortOption, showMembersOnly]);

  const filteredSummaries = useMemo(() => {
    if (selectedStoreId === 'all') return customerSummaries;
    return customerSummaries.filter((summary) => summary.storeId === selectedStoreId);
  }, [customerSummaries, selectedStoreId]);

  const customerStats = useMemo(() => {
    const stats: Record<
      string,
      {
        name: string;
        phone: string;
        userId: string;
        totalOrders: number;
        totalSpent: number;
        lastOrder: string;
        lastStoreId: string;
        activeItemCount: number;
        completedItemCount: number;
      }
    > = {};

    filteredSummaries.forEach((summary) => {
      let key = '';
      if (summary.userId) {
        key = `UID:${summary.userId}`;
      } else if (!showMembersOnly) {
        const cleanPhone = (summary.phone || '').replace(/\D/g, '');
        key = `GUEST-NAM:${summary.name}-TEL:${cleanPhone}`;
      } else {
        return;
      }

      if (!stats[key]) {
        stats[key] = {
          name: summary.name,
          phone: summary.phone || '',
          userId: summary.userId || '',
          totalOrders: 0,
          totalSpent: 0,
          lastOrder: summary.lastOrder,
          lastStoreId: summary.storeId,
          activeItemCount: 0,
          completedItemCount: 0,
        };
      }
      stats[key].totalOrders += summary.totalOrders;
      stats[key].totalSpent += summary.totalSpent;
      stats[key].activeItemCount += summary.activeItemCount;
      stats[key].completedItemCount += summary.completedItemCount;

      if (new Date(summary.lastOrder) > new Date(stats[key].lastOrder)) {
        stats[key].lastOrder = summary.lastOrder;
        stats[key].lastStoreId = summary.storeId;
      }
    });

    const values = Object.values(stats);
    if (sortOption === 'revenue') {
      return values.sort((a, b) => b.totalSpent - a.totalSpent);
    } else if (sortOption === 'latest') {
      return values.sort((a, b) => new Date(b.lastOrder).getTime() - new Date(a.lastOrder).getTime());
    } else {
      return values.sort((a, b) => {
        const getDisplayName = (stat: { name: string; phone: string; userId: string }) => {
          const cleanPhone = stat.phone ? stat.phone.replace(/[^0-9]/g, '') : '';
          const alias = customerAliases && cleanPhone ? customerAliases[cleanPhone] : '';
          return alias || stat.name || '';
        };
        return getDisplayName(a).localeCompare(getDisplayName(b), 'ko');
      });
    }
  }, [filteredSummaries, sortOption, customerAliases, showMembersOnly]);

  const filteredStats = useMemo(() => {
    let stats = customerStats;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      stats = stats.filter(stat => {
        const cleanPhone = stat.phone ? stat.phone.replace(/[^0-9]/g, '') : '';
        const alias = customerAliases && cleanPhone ? customerAliases[cleanPhone] : '';
        const isNameMatch = stat.name.toLowerCase().includes(query);
        const isAliasMatch = alias && alias.toLowerCase().includes(query);
        const isPhoneMatch = stat.phone.includes(query);
        return isNameMatch || isAliasMatch || isPhoneMatch;
      });
    }
    return stats;
  }, [customerStats, searchQuery, customerAliases]);

  const totalPages = Math.max(1, Math.ceil(filteredStats.length / ITEMS_PER_PAGE));
  const paginatedStats = useMemo(() => {
    return filteredStats.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }, [filteredStats, currentPage]);

  return (
    <div className="space-y-4">
      {/* Store Selector */}
      <div className="bg-white p-4 rounded-3xl border shadow-sm space-y-3">
        <div className="flex justify-between items-center px-1">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-gray-400" />
            <h3 className="text-sm font-black text-gray-900">판매지별 보기</h3>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 px-1">
          <button
            onClick={() => setSelectedStoreId('all')}
            className={`flex-shrink-0 px-4 py-2.5 rounded-2xl text-xs font-black transition-all ${selectedStoreId === 'all'
              ? 'bg-gray-900 text-white shadow-md shadow-gray-200'
              : 'bg-white text-gray-400 border border-gray-100 hover:border-gray-200'
              }`}
          >
            전체 매장
          </button>
          {stores.map(s => {
            const isSelected = selectedStoreId === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSelectedStoreId(s.id)}
                className={`flex-shrink-0 px-4 py-2.5 rounded-2xl transition-all border text-left flex flex-col justify-center ${isSelected
                  ? 'bg-emerald-50 border-emerald-200 ring-1 ring-emerald-500/20 shadow-sm'
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <div className={`text-xs font-black ${isSelected ? 'text-emerald-900' : 'text-gray-700'}`}>
                  {s.name}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white p-8 rounded-[32px] border shadow-sm">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-2xl">
              <Users size={20} />
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-900">
                {selectedStoreId === 'all' ? '전체 고객 통계' : `${stores.find(s => s.id === selectedStoreId)?.name || ''} 고객 통계`}
              </h3>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Customer Statistics</p>
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 rounded-full text-[11px] font-black text-gray-500">
            총 {customerStats.length}명
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
          <div className="bg-gray-50/50 p-6 rounded-[24px] border border-gray-100/50 transition-all hover:bg-gray-50">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter mb-2">누적 주문 건수</p>
            <p className="text-2xl font-black text-gray-900">{filteredSummaries.reduce((sum, summary) => sum + summary.totalOrders, 0)}<span className="text-sm ml-1 text-gray-400">건</span></p>
          </div>
          <div className="bg-emerald-50/30 p-6 rounded-[24px] border border-emerald-100/30 transition-all hover:bg-emerald-50/50">
            <p className="text-[10px] font-black text-emerald-500 uppercase tracking-tighter mb-2">누적 결제 금액</p>
            <p className="text-2xl font-black text-emerald-600">{filteredSummaries.reduce((sum, summary) => sum + summary.totalSpent, 0).toLocaleString()}<span className="text-sm ml-1 text-gray-400">원</span></p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <>
          <div className="flex items-center justify-between pl-1">
            <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">
              고객 리스트 ({sortOption === 'revenue' ? '매출순' : sortOption === 'latest' ? '최신순' : '이름순'})
            </h4>
            <div className="flex items-center gap-2">
              <div className="flex bg-gray-100/50 p-1 rounded-xl">
                <button
                  onClick={() => setSortOption('revenue')}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-black transition-all ${sortOption === 'revenue'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  매출순
                </button>
                <button
                  onClick={() => setSortOption('latest')}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-black transition-all ${sortOption === 'latest'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  최신순
                </button>
                <button
                  onClick={() => setSortOption('name')}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-black transition-all ${sortOption === 'name'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  이름순
                </button>
              </div>
              <button
                onClick={() => setShowMembersOnly(!showMembersOnly)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-black transition-all border ${showMembersOnly
                  ? 'bg-emerald-50 text-emerald-600 border-emerald-200 shadow-sm'
                  : 'bg-white text-gray-400 border-gray-100 hover:border-gray-200'
                  }`}
              >
                <User size={14} />
                {showMembersOnly ? '회원 전용' : '모두 보기'}
              </button>
            </div>
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="고객명 또는 닉네임으로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white px-5 py-4 pl-12 rounded-[24px] border border-gray-100 shadow-sm text-[13px] font-bold outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all placeholder:text-gray-300"
            />
            <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300" />
          </div>

          {filteredStats.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block bg-white rounded-[32px] border shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">고객명 (닉네임)</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">연락처</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-right w-24">주문 횟수</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-right w-32">누적 결제액</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-center w-48">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {paginatedStats.map((stat, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-black text-gray-900">
                              {(customerAliases && stat.phone && customerAliases[stat.phone.replace(/[^0-9]/g, '')]) ? (
                                <>
                                  <span className="text-emerald-600">{customerAliases[stat.phone.replace(/[^0-9]/g, '')]}</span>
                                  <span className="text-gray-400 text-[11px] ml-1">({stat.name})</span>
                                </>
                              ) : stat.name}
                            </p>
                            {stat.userId && (
                              <span className="text-[9px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full font-black">회원</span>
                            )}
                            {!stat.userId && (
                              <span className="text-[9px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded-full font-black">비회원</span>
                            )}
                            {stat.activeItemCount > 0 && (
                              <span className="text-[9px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-black">
                                진행중 {stat.activeItemCount}
                              </span>
                            )}
                            {stat.completedItemCount > 0 && (
                              <span className="text-[9px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full font-black">
                                구매완료 {stat.completedItemCount}
                              </span>
                            )}
                            {onUpdateAlias && stat.phone && (
                              <button
                                onClick={() => {
                                  const currentAlias = customerAliases?.[stat.phone.replace(/[^0-9]/g, '')] || '';
                                  const newAlias = window.prompt(`'${stat.name}' 고객님의 새 닉네임을 입력하세요.`, currentAlias);
                                  if (newAlias !== null) {
                                    onUpdateAlias(stat.phone.replace(/[^0-9]/g, ''), newAlias);
                                  }
                                }}
                                className="text-[9px] font-black text-gray-400 hover:text-emerald-600 bg-gray-50 px-1.5 py-0.5 rounded transition-colors group-hover:bg-emerald-50 group-hover:text-emerald-600"
                              >
                                닉네임 수정
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs font-bold text-gray-500">
                          {formatPhoneNumber(stat.phone)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-black text-gray-900">
                          {stat.totalOrders}회
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-black text-emerald-600">
                          {stat.totalSpent.toLocaleString()}원
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-center gap-2">
                            {onViewHistory && (
                              <button
                                onClick={() => onViewHistory(stat.name, stat.phone, stat.userId)}
                                className="flex items-center gap-1.5 text-[10px] font-black bg-emerald-50 text-emerald-600 px-3 py-2 rounded-xl hover:bg-emerald-100 transition-colors"
                              >
                                <History size={14} /> 주문 내역
                              </button>
                            )}
                            {onOpenAdminOrder && (
                              <button
                                onClick={() => {
                                  let storeId = selectedStoreId !== 'all' ? selectedStoreId : stat.lastStoreId;
                                  let orderSheetId = undefined;

                                  if (storeId) {
                                    const storeSheets = orderSheets
                                      .filter(s => s.storeId === storeId && !s.isClosed)
                                      .sort((a, b) => new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime());

                                    if (storeSheets.length > 0) {
                                      orderSheetId = storeSheets[0].id;
                                    } else {
                                      const allStoreSheets = orderSheets
                                        .filter(s => s.storeId === storeId)
                                        .sort((a, b) => new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime());
                                      if (allStoreSheets.length > 0) {
                                        orderSheetId = allStoreSheets[0].id;
                                      }
                                    }
                                  }
                                  onOpenAdminOrder(stat.name, stat.phone, undefined, storeId, orderSheetId, stat.userId);
                                }}
                                className="text-[10px] font-black bg-gray-900 text-white px-3 py-2 rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
                              >
                                대신 주문
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile View */}
              <div className="md:hidden grid grid-cols-1 gap-4">
                {paginatedStats.map((stat, idx) => (
                  <div key={idx} className="bg-white p-5 rounded-[28px] border shadow-sm space-y-4 transition-all hover:shadow-md">
                    <div className="flex justify-between items-start gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <p className="text-[15px] font-black text-gray-900 truncate">
                            {(customerAliases && stat.phone && customerAliases[stat.phone.replace(/[^0-9]/g, '')]) ? (
                              <>
                                <span className="text-emerald-600">{customerAliases[stat.phone.replace(/[^0-9]/g, '')]}</span>
                                <span className="text-gray-400 text-[11px] ml-1">({stat.name})</span>
                              </>
                            ) : stat.name}
                          </p>
                          {stat.userId && (
                            <span className="text-[9px] bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full font-black">회원</span>
                          )}
                          {!stat.userId && (
                            <span className="text-[9px] bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-black">비회원</span>
                          )}
                          {onUpdateAlias && stat.phone && (
                            <button
                              onClick={() => {
                                const currentAlias = customerAliases?.[stat.phone.replace(/[^0-9]/g, '')] || '';
                                const newAlias = window.prompt(`'${stat.name}' 고객님의 새 닉네임을 입력하세요.`, currentAlias);
                                if (newAlias !== null) {
                                  onUpdateAlias(stat.phone.replace(/[^0-9]/g, ''), newAlias);
                                }
                              }}
                              className="text-[9px] font-black text-gray-400 bg-gray-50 px-2 py-0.5 rounded"
                            >
                              수정
                            </button>
                          )}
                          {stat.activeItemCount > 0 && (
                            <span className="text-[9px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-black">
                              진행중 {stat.activeItemCount}
                            </span>
                          )}
                          {stat.completedItemCount > 0 && (
                            <span className="text-[9px] bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full font-black">
                              구매완료 {stat.completedItemCount}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] font-bold text-gray-400">{formatPhoneNumber(stat.phone)}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-black text-emerald-600">{stat.totalSpent.toLocaleString()}원</p>
                        <div className="flex items-center justify-end gap-1.5 mt-0.5">
                          <span className="text-[10px] font-bold text-gray-300">주문</span>
                          <span className="text-[10px] font-black text-gray-900">{stat.totalOrders}회</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {onViewHistory && (
                        <button
                          onClick={() => onViewHistory(stat.name, stat.phone, stat.userId)}
                          className="flex items-center justify-center gap-1.5 text-[10px] font-black bg-emerald-50 text-emerald-600 py-3 rounded-2xl hover:bg-emerald-100 transition-colors"
                        >
                          <History size={14} /> 주문 내역
                        </button>
                      )}
                      {onOpenAdminOrder && (
                        <button
                          onClick={() => {
                            let storeId = selectedStoreId !== 'all' ? selectedStoreId : stat.lastStoreId;
                            let orderSheetId = undefined;

                            if (storeId) {
                              const storeSheets = orderSheets
                                .filter(s => s.storeId === storeId && !s.isClosed)
                                .sort((a, b) => new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime());

                              if (storeSheets.length > 0) {
                                orderSheetId = storeSheets[0].id;
                              } else {
                                const allStoreSheets = orderSheets
                                  .filter(s => s.storeId === storeId)
                                  .sort((a, b) => new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime());
                                if (allStoreSheets.length > 0) {
                                  orderSheetId = allStoreSheets[0].id;
                                }
                              }
                            }
                            onOpenAdminOrder(stat.name, stat.phone, undefined, storeId, orderSheetId, stat.userId);
                          }}
                          className="flex items-center justify-center gap-1.5 text-[10px] font-black bg-gray-900 text-white py-3 rounded-2xl hover:bg-gray-800 transition-colors"
                        >
                          대신 주문하기
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6 pb-6">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border text-xs font-bold bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    이전
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, i, arr) => (
                        <React.Fragment key={p}>
                          {i > 0 && arr[i - 1] !== p - 1 ? <span className="text-gray-400 text-xs px-1">...</span> : null}
                          <button
                            onClick={() => setCurrentPage(p)}
                            className={`min-w-[32px] h-8 rounded-lg text-xs font-bold transition-colors ${currentPage === p
                              ? 'bg-gray-900 text-white shadow-sm'
                              : 'bg-white border text-gray-600 hover:bg-gray-50'
                              }`}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      ))
                    }
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border text-xs font-bold bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    다음
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="py-20 text-center bg-white rounded-[32px] border border-dashed border-gray-200">
              <div className="inline-flex p-4 bg-gray-50 text-gray-400 rounded-full mb-4">
                <Users size={32} />
              </div>
              <p className="text-sm font-black text-gray-900">검색 결과가 없습니다.</p>
              <p className="text-xs font-bold text-gray-400 mt-1">다른 매장 또는 검색어를 입력해보세요.</p>
            </div>
          )}
        </>
      </div>
    </div>
  );
};
